import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine } from "./motion/Reveal";
import StarField from "./StarField";
import AngelWings from "./AngelWings";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yGlow = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const yContent = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden aura pt-24 pb-16"
        >
            <StarField count={28} />
            <motion.div
                style={{ y: yGlow }}
                className="absolute -top-24 left-1/2 -translate-x-1/2 h-[560px] w-[560px] rounded-full bg-gradient-to-b from-alma-goldSoft/60 via-alma-gold/10 to-transparent blur-3xl"
                aria-hidden
            />
            <motion.div
                style={{ y: yGlow }}
                className="absolute top-1/3 -left-16 h-64 w-64 rounded-full bg-alma-sky/20 blur-3xl animate-float-slower"
                aria-hidden
            />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-alma-white to-transparent" aria-hidden />

            <motion.div style={{ y: yContent, opacity }} className="relative max-w-[1100px] mx-auto w-full px-6 sm:px-8 text-center">
                {/* Angel wings — first thing seen */}
                <div className="flex justify-center mb-2 sm:mb-4">
                    <AngelWings className="w-[260px] sm:w-[360px] h-auto animate-float-slow" />
                </div>

                <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em] text-alma-ink">
                    <MaskLine delay={0.55}>El hogar de</MaskLine>
                    <MaskLine delay={0.68} className="italic font-light">
                        <span className="text-shimmer">tu alma</span>
                    </MaskLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1 }}
                    className="mx-auto mt-8 max-w-2xl font-sans text-lg sm:text-xl text-alma-slate leading-relaxed"
                >
                    Una comunidad espiritual donde sentirte acompañada, comprendida y sostenida
                    mientras aprendes a integrar la espiritualidad en tu vida cotidiana.
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.12 }}
                    className="mx-auto mt-4 max-w-xl font-sans text-base text-alma-slate/85 leading-relaxed"
                >
                    Un lugar donde puedes ser tú, compartir lo que estás viviendo y caminar junto a
                    personas que hablan tu mismo idioma espiritual.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.24 }}
                    className="mt-10 flex flex-col items-center gap-4"
                >
                    <a
                        href="#precios"
                        data-testid="hero-cta"
                        className="group relative inline-flex items-center justify-center rounded-full bg-gradient-to-r from-alma-gold to-alma-goldDeep text-alma-ink h-14 px-9 text-base font-semibold shadow-lg shadow-alma-gold/30 overflow-hidden"
                    >
                        <span className="absolute inset-0 bg-alma-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                        <span className="relative group-hover:text-alma-white transition-colors">
                            Quiero formar parte de Alquimia del Alma
                        </span>
                    </a>
                    <p className="text-sm text-alma-slate/80">Desde 27 € al mes · Sin permanencia mensual</p>
                </motion.div>
            </motion.div>

            <motion.a
                href="#esencia"
                style={{ opacity }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-alma-slate/60 hover:text-alma-ink transition-colors"
            >
                <ArrowDown className="h-4 w-4 animate-bounce" />
            </motion.a>
        </section>
    );
}
